import React, { Dispatch, SetStateAction, useEffect } from 'react';

import { css } from '@emotion/react';
import { useQueryClient } from 'react-query';
import { useSetRecoilState } from 'recoil';

import Button from '@components/Common/Button';

import {
  accessTokenStorage,
  expirationDateStorage,
} from '@storage/authStorage';

import { useInternalRouter } from '@hooks/useInternalRouter';

import { palette } from '@styles/theme';

import { auth } from '@constants/state';

import ModalPortal from './ModalPortal';

interface Props {
  open: boolean;
  handleGlobalState?: Dispatch<SetStateAction<boolean>>;
}

/** 메인 컴포넌트: 세션만료 모달 */
const SessionExpiredModal = ({ open, handleGlobalState }: Props) => {
  const queryClient = useQueryClient();
  const router = useInternalRouter();
  const setAuth = useSetRecoilState(auth);

  /** 로그인 하러가기 클릭 시 */
  const handleLogin = () => {
    handleGlobalState && handleGlobalState(false);
    setAuth({
      isLogin: false,
      name: '',
      email: '',
      role: undefined,
      company: '',
      companyId: undefined,
    });
    accessTokenStorage.clear();
    expirationDateStorage.clear();
    localStorage.removeItem('authentication');
    queryClient.clear();
    router.replace('/login');
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <ModalPortal>
      <div css={[container, open && openModal]}>
        <div css={modal}>
          <div css={header}>로그인 세션만료</div>
          <div css={contents}>
            <div>
              로그인 세션이 만료 되었습니다.
              <br />
              다시 로그인해 주세요.
            </div>
            <div css={buttonWrap}>
              <Button
                label="로그인 하러가기"
                theme="secondary"
                handleClick={handleLogin}
              />
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default SessionExpiredModal;

const container = css`
  display: none;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.4);

  @keyframes modal-bg-show {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const openModal = css`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: modal-bg-show 0.3s;
`;

const modal = css`
  position: relative;
  width: 360px;
  height: auto;
  background-color: #ffffff;
  box-shadow: 0px 5px 16px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
`;

const header = css`
  display: flex;
  position: relative;
  min-height: 72px;
  max-height: 94px;
  justify-content: flex-start;
  padding-left: 20px;
  align-items: center;
  font-size: 20px;
  font-weight: 700;
  color: #222;
  border-bottom: 1px solid ${palette.gray3};
`;

const contents = css`
  display: flex;
  flex-direction: column;
  padding: 30px;
`;

const buttonWrap = css`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
`;
